/**
 * EntitlementPaywall — soft-lock UI for gated features.
 *
 * Iter 163 · When an endpoint returns HTTP 402 (see
 * `lib/axios-interceptors.js`), the caller should catch the resulting
 * `EntitlementRequiredError` and render this component instead of the
 * feature. Empathetic, on-brand copy that explains WHAT'S locked and
 * WHO to contact.
 */
import React from 'react';
import { Sparkles, Mail, ArrowRight } from 'lucide-react';

const FRIENDLY_FLAG_LABEL = {
  legacy_family_marketplace: 'Family Marketplace',
  legacy_ops_back_office:    'Ops Back-Office',
  rcm_billing:               'Revenue Cycle Billing',
  discharge_referral_intake: 'Discharge Intake',
  placement_orchestration:   'Placement Orchestration',
  readmission_watch:         'Readmission Watch',
  hospital_scoreboard:       'Hospital Scoreboard',
  llm_assist_phi:            'AI-Assisted PHI Notes',
};

export default function EntitlementPaywall({
  flag,
  title,
  message,
  contactHref = 'mailto:ops@caraloom.com?subject=Enable%20feature',
  primaryHref,
  primaryLabel,
  className = '',
  compact = false,
}) {
  const label = FRIENDLY_FLAG_LABEL[flag] || (flag ? flag.replace(/_/g, ' ') : 'This feature');
  const heading = title || `${label} isn't turned on for your account yet.`;
  const body = message || (
    `We can flip this on for you — reach the CaraLoom team below and we'll enable it in minutes.`
  );

  if (compact) {
    return (
      <div
        className={`rounded-xl border border-slate-200 bg-slate-50 p-4 flex items-start gap-3 ${className}`}
        data-testid="entitlement-paywall"
        data-flag={flag || ''}
      >
        <Sparkles className="w-4 h-4 text-slate-500 flex-shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-slate-900">{heading}</div>
          <p className="text-xs text-slate-600 mt-1">{body}</p>
        </div>
        <a
          href={contactHref}
          className="text-xs text-slate-700 hover:text-slate-950 inline-flex items-center gap-1 flex-shrink-0"
          data-testid="entitlement-paywall-contact"
        >
          <Mail className="w-3 h-3" /> Contact ops
        </a>
      </div>
    );
  }

  return (
    <div
      className={`rounded-2xl border border-slate-200 bg-gradient-to-br from-slate-50 to-white p-8 max-w-xl mx-auto text-center ${className}`}
      data-testid="entitlement-paywall"
      data-flag={flag || ''}
    >
      <div className="w-12 h-12 mx-auto rounded-full bg-cn-primary/10 flex items-center justify-center mb-4">
        <Sparkles className="w-5 h-5 text-cn-primary" />
      </div>
      <h2 className="text-lg font-semibold text-slate-900">{heading}</h2>
      <p className="text-sm text-slate-600 mt-2">{body}</p>
      <div className="flex flex-col sm:flex-row items-center justify-center gap-2 mt-5">
        {primaryHref && (
          <a
            href={primaryHref}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-slate-900 text-white text-sm font-medium hover:bg-slate-800"
            data-testid="entitlement-paywall-primary"
          >
            {primaryLabel || 'Explore alternatives'} <ArrowRight className="w-3.5 h-3.5" />
          </a>
        )}
        <a
          href={contactHref}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-slate-200 text-slate-700 text-sm font-medium hover:bg-slate-50"
          data-testid="entitlement-paywall-contact"
        >
          <Mail className="w-3.5 h-3.5" /> Ask CaraLoom Ops
        </a>
      </div>
    </div>
  );
}
